import { Trans } from '@lingui/react/macro';
import { MoneyRecord, OpenOrderRecord, ShoppingCartRecord } from '@navio/nsdk';
import { NavioCar } from 'noddi-ui-web';
import { catalogPriceMapKey, formatCurrencyAmount, getCarDisplayName, type ProposalCatalogPrice } from 'noddi-util';
import { CatalogPrice, PriceListDiscountLabel } from 'noddi-web';
import { useMembershipProgramImages } from '../../hooks/useMembershipProgramImages';
import { toCheckoutDiscountRows } from '../../utils/checkoutDiscounts';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { CheckoutDiscountRow } from '../Summary/CheckoutDiscountRow';
import { getProposalSummaryDecimals } from './proposalSummaryDecimals';

type Props = {
  /** The proposal's group — it prices the cart, so it owns the discount logos too. */
  userGroupId: number;
  shoppingCart: ShoppingCartRecord;
  openOrder?: OpenOrderRecord | null;
  wrapCarsInCard?: boolean;
  adHocPrices?: Map<string, MoneyRecord>;
  catalogPriceByKey?: Map<string, ProposalCatalogPrice>;
};

export const ProposalShoppingCartSummary = ({
  userGroupId,
  shoppingCart,
  openOrder,
  wrapCarsInCard = true,
  adHocPrices,
  catalogPriceByKey
}: Props) => {
  const { cars, couponDiscounts, membershipDiscounts, totalPrice } = shoppingCart;
  const { imageByDiscountId } = useMembershipProgramImages(userGroupId);
  const priceListDiscountLabel = getCommonTranslations().priceListDiscountLabel;
  const discountRows = toCheckoutDiscountRows({
    couponDiscounts,
    membershipDiscounts,
    imageByDiscountId
  });
  const couponRows = discountRows.filter((row) => row.kind === 'coupon');
  const membershipRows = discountRows.filter((row) => row.kind === 'membership');
  const totalDiscount = discountRows.reduce((acc, row) => acc + Math.abs(row.amount.amount), 0);

  // Resolved once so the lines and the total cannot drift apart.
  const carRows = cars.map((car) => ({
    car,
    lines: [...car.salesItems]
      .sort((a, b) => b.price.priceGross.amount - a.price.priceGross.amount)
      .map(({ name, price, salesItemId, selectedQuantity }) => {
        const key = catalogPriceMapKey(car.licensePlate.number, salesItemId);
        const catalog = catalogPriceByKey?.get(key);
        const unitPrice = catalog?.grossPrice ?? adHocPrices?.get(key) ?? price.priceGross;
        const quantity = selectedQuantity ?? 1;

        return {
          name,
          catalog,
          quantity,
          unitPrice,
          linePrice: { ...unitPrice, amount: unitPrice.amount * quantity },
          originalLinePrice: catalog?.originalGrossPrice
            ? { ...catalog.originalGrossPrice, amount: catalog.originalGrossPrice.amount * quantity }
            : null
        };
      })
  }));

  const computedTotal = carRows.reduce(
    (acc, { lines }) => acc + lines.reduce((carAcc, line) => carAcc + line.linePrice.amount, 0),
    0
  );

  const displayTotal = computedTotal - totalDiscount;

  const externalOrderRows = (openOrder?.lines ?? []).map((line, index) => ({
    key: `external-open-order-${openOrder?.externalOrderId ?? 'open'}-${index}`,
    name: line.name,
    quantity: line.quantity,
    linePrice: line.grossAmount
  }));

  const openOrderTotal = externalOrderRows.reduce((acc, line) => acc + line.linePrice.amount, 0);

  const decimals = getProposalSummaryDecimals([
    displayTotal + openOrderTotal,
    ...carRows.flatMap(({ lines }) =>
      lines.flatMap((line) => [line.linePrice.amount, line.originalLinePrice?.amount ?? 0])
    )
  ]);

  return (
    <div className='flex flex-col gap-2'>
      {carRows.map(({ car, lines }) => (
        <div key={car.licensePlate.number}>
          <div className='flex items-center justify-between gap-4'>
            <h2 className='font-semibold'>{getCarDisplayName({ car, showYear: false })}</h2>
            {wrapCarsInCard && <NavioCar scale={0.4} />}
          </div>

          <div className='flex flex-col'>
            {lines.map(({ name, catalog, quantity, unitPrice, linePrice, originalLinePrice }) => (
              <div key={`${car.licensePlate.number} - ${name}`} className='flex w-full justify-between gap-4'>
                <div className='flex flex-col'>
                  <span>{name}</span>
                  {quantity > 1 && (
                    <span className='text-sm text-muted-foreground'>
                      {quantity}x {formatCurrencyAmount({ money: unitPrice, decimals })}
                    </span>
                  )}
                </div>
                <span className='flex flex-col items-end'>
                  <CatalogPrice payable={linePrice} original={originalLinePrice} align='end' decimals={decimals} />
                  <PriceListDiscountLabel
                    priceListName={catalog?.priceListName}
                    discountPercent={catalog?.discountPercent}
                    translations={priceListDiscountLabel}
                  />
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {externalOrderRows.length > 0 && (
        <div className='flex flex-col gap-1'>
          <span className='font-semibold'>
            <Trans>Existing items</Trans>
          </span>
          {externalOrderRows.map(({ key, name, quantity, linePrice }) => (
            <div key={key} className='flex justify-between gap-4 text-muted-foreground'>
              <span>{quantity !== 1 ? `${quantity}x ${name}` : name}</span>
              <span>{formatCurrencyAmount({ money: linePrice, decimals })}</span>
            </div>
          ))}
        </div>
      )}

      {couponRows.length > 0 && (
        <div className='flex flex-col gap-1'>
          <span className='font-semibold'>
            <Trans>Discounts</Trans>
          </span>
          {couponRows.map((row) => (
            <CheckoutDiscountRow key={row.key} label={row.label} amount={row.amount} />
          ))}
        </div>
      )}

      {membershipRows.length > 0 && (
        <div className='flex flex-col gap-1'>
          <span className='font-semibold'>
            <Trans>Membership benefits</Trans>
          </span>
          {membershipRows.map((row) => (
            <CheckoutDiscountRow key={row.key} label={row.label} amount={row.amount} imageUrl={row.imageUrl} />
          ))}
        </div>
      )}

      <div className='flex justify-between gap-4 border-t border-border pt-2'>
        <span>
          <Trans>Total price</Trans>
        </span>
        <span className='font-semibold'>
          {formatCurrencyAmount({
            money: { amount: displayTotal + openOrderTotal, currency: totalPrice.currency },
            decimals
          })}
        </span>
      </div>
    </div>
  );
};
